// src/pages/LessonViewer.jsx
import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle,
  X,
  ChevronRight,
  Award,
  Clock,
  Wifi,
  WifiOff,
  Download,
  Home,
} from "lucide-react";

// ✅ Safe API_BASE (no standalone import.meta)
const API_BASE =
  (process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE &&
    import.meta.env.VITE_API_BASE.replace(/\/+$/, "")) ||
  "http://localhost:5050";

// ✅ Base path for static files (lessons), respects CRA PUBLIC_URL / homepage
const PUBLIC_BASE = process.env.PUBLIC_URL || "";

// Map any legacy/alt slugs to the filesystem slug we actually serve
function fsSlug(raw) {
  const s = String(raw || "").toLowerCase();
  if (
    s === "focus-family" ||
    s === "focus_on_the_family" ||
    s === "focus-on-the-family" ||
    s === "focus"
  ) {
    return "family";
  }
  if (s === "danielrevelation" || s === "daniel_revelation") return "daniel-revelation";
  return s;
}

async function req(path, { method = "GET", body, token } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const json = isJson ? await res.json().catch(() => ({})) : null;
  if (!res.ok) {
    const msg = json?.error || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return json?.data ?? json;
}

// 🧠 Cache the detected extension per FS courseId to avoid probing every time
const lessonExtCache = new Map(); // fsCourseId -> ".htm" | ".html"

async function detectLessonExtension(fsCourseId, lessonNumber) {
  if (lessonExtCache.has(fsCourseId)) return lessonExtCache.get(fsCourseId);

  const tryExt = async (ext) => {
    try {
      const url = `${PUBLIC_BASE}/lessons/${fsCourseId}/lesson${lessonNumber}${ext}`;
      const res = await fetch(url, { method: "HEAD" });
      return res.ok ? ext : null;
    } catch {
      return null;
    }
  };

  // Try .htm first, then .html
  const found = (await tryExt(".htm")) || (await tryExt(".html")) || ".htm";
  lessonExtCache.set(fsCourseId, found);
  return found;
}

export default function LessonViewer() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  // Use the slug from the URL for API calls, but normalize for filesystem paths
  const apiCourseId = courseId; // what DB expects
  const fsCourseId = fsSlug(courseId); // what lives under /public/lessons/*

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lessonUrl, setLessonUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [isCached, setIsCached] = useState(false);

  const [lessonList, setLessonList] = useState([]);
  const [detectedExt, setDetectedExt] = useState(".htm"); // default; will auto-detect
  const [completedLessons, setCompletedLessons] = useState(() => {
    try {
      const raw = localStorage.getItem(`progress:${apiCourseId}`);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const iframeRef = useRef(null);

  // Network status
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Load lessons metadata strictly from backend and detect extension
  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);

      // 1) Detect file extension (HEAD probe once) using fsCourseId
      const ext = await detectLessonExtension(fsCourseId, 1);
      if (mounted) setDetectedExt(ext);

      // 2) Load lessons from backend content API (required source of truth)
      try {
        const meta = await req(`/api/content/courses/${apiCourseId}`);
        // Expecting { lessons: [{number,title}, ...] }
        const metaLessons = Array.isArray(meta?.lessons) ? meta.lessons : [];
        const normalized = metaLessons
          .map((l) => ({
            id: Number(l.number),
            title: l.title || `Lesson ${Number(l.number)}`,
            path: `${PUBLIC_BASE}/lessons/${fsCourseId}/lesson${Number(l.number)}${ext}`,
          }))
          .filter((l) => Number.isFinite(l.id))
          .sort((a, b) => a.id - b.id);

        if (mounted) setLessonList(normalized);
      } catch (e) {
        console.error("Failed to load /api/content for lessons:", e);
        if (mounted) setLessonList([]); // no client fallbacks
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [apiCourseId, fsCourseId]);

  // Seed progress from server (merge with local)
  useEffect(() => {
    let cancelled = false;
    async function hydrateFromServer() {
      try {
        const token = await getToken().catch(() => null);
        if (!token) return;
        const server = await req(`/api/progress/me/${apiCourseId}`, { token });
        const serverCompleted = new Set(server?.lessonsCompleted || []);
        if (!cancelled) {
          setCompletedLessons((prev) => {
            const merged = { ...prev };
            for (const n of serverCompleted) merged[String(n)] = true;
            return merged;
          });
        }
      } catch {
        // ignore quietly if backend not ready/offline
      }
    }
    hydrateFromServer();
    return () => {
      cancelled = true;
    };
  }, [apiCourseId, getToken]);

  // Persist to localStorage (keyed by API slug)
  useEffect(() => {
    try {
      localStorage.setItem(`progress:${apiCourseId}`, JSON.stringify(completedLessons));
    } catch {}
  }, [apiCourseId, completedLessons]);

  // Sync lesson URL when route or list changes
  useEffect(() => {
    if (!lessonId) return;

    const current = lessonList.find((l) => l.id === Number(lessonId));
    const url =
      current?.path || `${PUBLIC_BASE}/lessons/${fsCourseId}/lesson${lessonId}${detectedExt}`;

    setLessonUrl(url);
    setLoading(true);

    const t = setTimeout(() => {
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (iframeRef.current) {
        iframeRef.current.classList.add("opacity-0");
        setTimeout(() => {
          iframeRef.current && iframeRef.current.classList.remove("opacity-0");
        }, 120);
      }
    }, 350);

    if ("caches" in window) {
      caches.match(url).then((resp) => setIsCached(!!resp));
    }

    return () => clearTimeout(t);
  }, [lessonId, fsCourseId, lessonList, detectedExt]);

  // Record "visit" on lesson open (idempotent) — use API slug
  useEffect(() => {
    async function pingVisited() {
      try {
        const token = await getToken().catch(() => null);
        if (!token) return;
        await req(`/api/progress/visit`, {
          method: "POST",
          body: { courseSlug: apiCourseId, lessonNumber: Number(lessonId) },
          token,
        });
      } catch {
        // ignore (endpoint missing / offline / CORS)
      }
    }
    if (lessonId) pingVisited();
  }, [apiCourseId, lessonId, getToken]);

  async function markCompleteServer() {
    try {
      const token = await getToken().catch(() => null);
      if (!token) return;
      await req(`/api/progress/complete`, {
        method: "POST",
        body: { courseSlug: apiCourseId, lessonNumber: Number(lessonId) },
        token,
      });
    } catch {
      // ignore silently if server not reachable
    }
  }

  const handleMarkComplete = async () => {
    // Optimistic local
    setCompletedLessons((prev) => ({ ...prev, [String(lessonId)]: true }));
    // Fire & forget to server
    markCompleteServer();
    // Move to test
    setTimeout(() => {
      navigate(`/courses/${apiCourseId}/lessons/${lessonId}/test`);
    }, 650);
  };

  const handleTakeTest = () => {
    navigate(`/courses/${apiCourseId}/lessons/${lessonId}/test`);
  };

  const totalLessons = useMemo(() => lessonList.length, [lessonList]);

  // ✅ completedCount only from real lessons
  const completedCount = useMemo(() => {
    return (lessonList || []).filter((l) => completedLessons[String(l.id)]).length;
  }, [lessonList, completedLessons]);

  // 🔒 Clamp progress between 0 and 100
  const progressPercentage = useMemo(() => {
    if (!totalLessons) return 0;
    const raw = Math.round((completedCount / totalLessons) * 100);
    return Math.min(100, Math.max(0, raw));
  }, [completedCount, totalLessons]);

  // Current lesson number + gating for prev/next
  const currentLessonNumber = Number(lessonId) || 1;
  const canGoPrev = currentLessonNumber > 1;
  const canGoNext =
    currentLessonNumber < (lessonList.length || 0) && !!completedLessons[String(lessonId)]; // ✅ only if current lesson is completed

  const goPrev = () => navigate(`/courses/${apiCourseId}/lessons/${currentLessonNumber - 1}`);
  const goNext = () => navigate(`/courses/${apiCourseId}/lessons/${currentLessonNumber + 1}`);

  // Pretty title from slug (treat both the same)
  const prettyCourseTitle = useMemo(() => {
    const map = {
      "focus-family": "Focus on the Family",
      family: "Focus on the Family",
      "daniel-revelation": "Daniel & Revelation",
      discover: "Discover",
      ugunduzi: "Ugunduzi",
      health: "Health",
    };
    if (map[apiCourseId]) return map[apiCourseId];
    if (map[fsCourseId]) return map[fsCourseId];
    return String(apiCourseId || "")
      .split("-")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");
  }, [apiCourseId, fsCourseId]);

  const isCompleted = !!completedLessons[String(lessonId)];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-orange-50 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900">
      <Navbar />

      {/* Sub-header (lesson chrome) */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-orange-100 dark:bg-slate-950/70 dark:border-slate-800">
        <div className="px-4 sm:px-6">
          <div className="max-w-6xl mx-auto py-4">
            {!isOnline && (
              <div className="mb-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-3 flex items-center justify-center gap-2 shadow-lg">
                <WifiOff size={18} />
                <span className="font-semibold text-sm">You’re offline — reading cached lesson</span>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  onClick={() => navigate(-1)}
                  className="group flex items-center justify-center w-10 h-10 rounded-2xl bg-orange-50 hover:bg-orange-100 text-orange-700 transition dark:bg-orange-500/10 dark:hover:bg-orange-500/15 dark:text-orange-200"
                  aria-label="Go back"
                >
                  <ArrowLeft size={20} className="group-hover:-translate-x-0.5 transition-transform" />
                </button>

                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white truncate max-w-[70vw]">
                      {prettyCourseTitle}
                    </h1>

                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold border ${
                        isOnline
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:border-emerald-500/30"
                          : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/30"
                      }`}
                    >
                      {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
                      {isOnline ? "Online" : "Offline"}
                    </span>

                    {isCached && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-500/10 dark:text-orange-200 dark:border-orange-500/30">
                        <Download size={12} />
                        Available Offline
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">
                    Lesson {lessonId} of {totalLessons || "—"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate("/dashboard/learner")}
                  className="inline-flex items-center gap-2 rounded-2xl border border-orange-200 bg-white px-4 py-2.5 text-sm font-bold text-orange-700 hover:bg-orange-50 transition dark:bg-slate-900 dark:border-slate-700 dark:text-orange-200 dark:hover:bg-slate-800"
                  title="Back to Dashboard"
                >
                  <Home size={18} />
                  <span className="hidden sm:inline">Dashboard</span>
                </button>

                <button
                  onClick={() => setSidebarOpen(true)}
                  className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-4 py-2.5 text-sm font-extrabold shadow-lg hover:opacity-95 transition"
                  title="View lessons"
                >
                  <BookOpen size={18} />
                  <span className="hidden md:inline">Lessons</span>
                </button>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4">
              <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden dark:bg-slate-800">
                <div
                  className="h-2 bg-gradient-to-r from-orange-600 to-amber-600 transition-all duration-500 ease-out"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2 text-xs text-slate-600 dark:text-slate-300">
                <span className="inline-flex items-center gap-1">
                  <Award size={14} className="text-orange-600 dark:text-orange-300" />
                  {completedCount} of {totalLessons || 0} completed
                </span>
                <span className="font-extrabold text-orange-700 dark:text-orange-200">
                  {progressPercentage}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 px-4 sm:px-6 pt-6 pb-28">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl border border-slate-200 bg-white/90 backdrop-blur-sm shadow-xl dark:bg-slate-950/60 dark:border-slate-800 overflow-hidden">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-[60vh] space-y-4 p-8">
                <div className="relative">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-200" />
                  <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600 absolute inset-0" />
                </div>
                <p className="text-slate-600 dark:text-slate-300 font-semibold animate-pulse">
                  Loading lesson...
                </p>
              </div>
            ) : (
              <div className="p-3 sm:p-5">
                <iframe
                  ref={iframeRef}
                  key={lessonUrl}
                  src={lessonUrl}
                  title={`Lesson ${lessonId}`}
                  className="w-full min-h-[72vh] rounded-2xl border border-slate-200 dark:border-slate-800 transition-opacity duration-500 ease-in-out bg-white dark:bg-slate-950"
                />

                {/* Desktop actions */}
                <div className="hidden sm:block mt-6">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:bg-slate-900/50 dark:border-slate-800">
                    <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
                      <div className="flex items-center gap-3">
                        {!isCompleted ? (
                          <div className="text-sm text-slate-600 dark:text-slate-300">
                            Mark this lesson complete to unlock the next lesson.
                          </div>
                        ) : (
                          <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-200">
                            Lesson completed — you can take the test.
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        {!isCompleted ? (
                          <button
                            onClick={handleMarkComplete}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-6 py-3 font-extrabold shadow-lg hover:opacity-95 transition"
                          >
                            <CheckCircle size={20} />
                            Mark as Complete
                          </button>
                        ) : (
                          <button
                            onClick={handleTakeTest}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-600 to-green-600 text-white px-6 py-3 font-extrabold shadow-lg hover:opacity-95 transition"
                          >
                            <Award size={20} />
                            Take Test
                          </button>
                        )}

                        <div className="flex items-center justify-between gap-3">
                          <button
                            onClick={goPrev}
                            disabled={!canGoPrev}
                            className={`inline-flex items-center justify-center gap-1 rounded-2xl px-5 py-3 font-bold border transition ${
                              canGoPrev
                                ? "border-slate-200 bg-white text-slate-800 hover:bg-slate-50 dark:bg-slate-900 dark:text-white dark:border-slate-700 dark:hover:bg-slate-800"
                                : "border-slate-200 bg-slate-50 text-slate-400 dark:bg-slate-900/40 dark:border-slate-800 dark:text-slate-500 cursor-not-allowed"
                            }`}
                          >
                            <ArrowLeft size={16} />
                            Prev
                          </button>

                          <button
                            onClick={goNext}
                            disabled={!canGoNext}
                            className={`inline-flex items-center justify-center gap-1 rounded-2xl px-5 py-3 font-bold border transition ${
                              canGoNext
                                ? "border-slate-200 bg-white text-slate-800 hover:bg-slate-50 dark:bg-slate-900 dark:text-white dark:border-slate-700 dark:hover:bg-slate-800"
                                : "border-slate-200 bg-slate-50 text-slate-400 dark:bg-slate-900/40 dark:border-slate-800 dark:text-slate-500 cursor-not-allowed"
                            }`}
                            title={!canGoNext ? "Mark this lesson complete to unlock next" : "Next lesson"}
                          >
                            Next
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Small hint on mobile (next gating) */}
                <div className="sm:hidden mt-4 text-xs text-slate-500 dark:text-slate-400 italic">
                  {canGoNext ? "Next lesson unlocked." : "Complete this lesson to unlock next."}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Sticky mobile action bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-xl dark:bg-slate-950/85 dark:border-slate-800">
        <div className="px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center gap-2">
            <button
              onClick={goPrev}
              disabled={!canGoPrev}
              className={`flex-1 inline-flex items-center justify-center gap-1 rounded-2xl py-3 font-extrabold border transition ${
                canGoPrev
                  ? "border-slate-200 bg-white text-slate-800 dark:bg-slate-900 dark:text-white dark:border-slate-700"
                  : "border-slate-200 bg-slate-50 text-slate-400 dark:bg-slate-900/40 dark:border-slate-800 dark:text-slate-500"
              }`}
            >
              <ArrowLeft size={16} />
              Prev
            </button>

            {!isCompleted ? (
              <button
                onClick={handleMarkComplete}
                className="flex-[1.4] inline-flex items-center justify-center gap-2 rounded-2xl py-3 font-extrabold bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-lg hover:opacity-95 transition"
              >
                <CheckCircle size={18} />
                Complete
              </button>
            ) : (
              <button
                onClick={handleTakeTest}
                className="flex-[1.4] inline-flex items-center justify-center gap-2 rounded-2xl py-3 font-extrabold bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-lg hover:opacity-95 transition"
              >
                <Award size={18} />
                Test
              </button>
            )}

            <button
              onClick={goNext}
              disabled={!canGoNext}
              className={`flex-1 inline-flex items-center justify-center gap-1 rounded-2xl py-3 font-extrabold border transition ${
                canGoNext
                  ? "border-slate-200 bg-white text-slate-800 dark:bg-slate-900 dark:text-white dark:border-slate-700"
                  : "border-slate-200 bg-slate-50 text-slate-400 dark:bg-slate-900/40 dark:border-slate-800 dark:text-slate-500"
              }`}
              title={!canGoNext ? "Complete this lesson to unlock next" : "Next lesson"}
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar / Lessons Drawer */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={() => setSidebarOpen(false)}
          />

          {/* Mobile bottom-sheet */}
          <div className="fixed inset-x-0 bottom-0 z-50 md:hidden">
            <div className="rounded-t-3xl bg-white shadow-2xl border border-slate-200 dark:bg-slate-950 dark:border-slate-800 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-500/10 dark:to-amber-500/10 dark:border-slate-800">
                <div>
                  <h2 className="text-lg font-extrabold text-slate-900 dark:text-white">
                    Course Lessons
                  </h2>
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                    {completedCount}/{lessonList.length || 0} completed
                  </p>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white hover:bg-orange-50 text-slate-600 hover:text-orange-700 transition dark:bg-slate-900 dark:hover:bg-slate-800 dark:text-slate-300"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-4 py-3 bg-gradient-to-r from-orange-600 to-amber-600 text-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold opacity-95">Progress</span>
                  <span className="text-xl font-extrabold">{progressPercentage}%</span>
                </div>
                <div className="bg-white/20 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-white transition-all duration-500" style={{ width: `${progressPercentage}%` }} />
                </div>
              </div>

              <div className="max-h-[55vh] overflow-y-auto px-3 py-3 space-y-2">
                {lessonList.map((lesson) => {
                  const id = lesson.id;
                  const done = !!completedLessons[String(id)];
                  const current = Number(lessonId) === id;
                  return (
                    <button
                      key={id}
                      onClick={() => {
                        navigate(`/courses/${apiCourseId}/lessons/${id}`);
                        setSidebarOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-2xl border transition ${
                        current
                          ? "border-orange-200 bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow"
                          : done
                          ? "border-emerald-200 bg-emerald-50 text-slate-800 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-white"
                          : "border-slate-200 bg-slate-50 text-slate-800 dark:bg-slate-900/40 dark:border-slate-800 dark:text-white"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-extrabold ${
                              current
                                ? "bg-white/15"
                                : done
                                ? "bg-emerald-600 text-white"
                                : "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            }`}
                          >
                            {id}
                          </div>
                          <div className="min-w-0">
                            <div className={`font-bold truncate ${current ? "text-white" : ""}`}>
                              {lesson.title}
                            </div>
                            <div className={`text-xs opacity-90 ${current ? "text-white/90" : "text-slate-500 dark:text-slate-300"}`}>
                              {done ? "Completed" : current ? "Now reading" : "Not completed"}
                            </div>
                          </div>
                        </div>

                        {done && !current ? (
                          <CheckCircle size={18} className={current ? "text-white" : "text-emerald-600 dark:text-emerald-200"} />
                        ) : null}
                        {current ? <Clock size={18} className="text-white animate-pulse" /> : null}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-3 border-t border-slate-200 dark:border-slate-800">
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="w-full rounded-2xl py-3 font-extrabold border border-slate-200 bg-white text-slate-800 dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                >
                  Close
                </button>
              </div>
            </div>
          </div>

          {/* Desktop right drawer */}
          <div className="hidden md:block fixed inset-y-0 right-0 z-50 w-[420px] max-w-full">
            <div className="bg-white h-full shadow-2xl border-l border-slate-200 dark:bg-slate-950 dark:border-slate-800 flex flex-col">
              <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-500/10 dark:to-amber-500/10 dark:border-slate-800">
                <div>
                  <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
                    Course Lessons
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">
                    {completedCount}/{lessonList.length || 0} completed
                  </p>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white hover:bg-orange-50 text-slate-600 hover:text-orange-700 transition dark:bg-slate-900 dark:hover:bg-slate-800 dark:text-slate-300"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-6 py-4 bg-gradient-to-r from-orange-600 to-amber-600 text-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold opacity-95">Progress</span>
                  <span className="text-2xl font-extrabold">{progressPercentage}%</span>
                </div>
                <div className="bg-white/20 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-white transition-all duration-500" style={{ width: `${progressPercentage}%` }} />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
                {lessonList.map((lesson) => {
                  const id = lesson.id;
                  const done = !!completedLessons[String(id)];
                  const current = Number(lessonId) === id;

                  return (
                    <button
                      key={id}
                      onClick={() => {
                        navigate(`/courses/${apiCourseId}/lessons/${id}`);
                        setSidebarOpen(false);
                      }}
                      className={`group relative flex items-center justify-between w-full text-left px-4 py-3.5 rounded-2xl border transition ${
                        current
                          ? "border-orange-200 bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow"
                          : done
                          ? "border-emerald-200 bg-emerald-50 text-slate-800 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-white"
                          : "border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100 dark:bg-slate-900/40 dark:border-slate-800 dark:text-white"
                      }`}
                    >
                      <span className="flex items-center gap-3 min-w-0">
                        <span
                          className={`flex items-center justify-center w-9 h-9 rounded-xl text-sm font-extrabold ${
                            current
                              ? "bg-white/15"
                              : done
                              ? "bg-emerald-600 text-white"
                              : "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                          }`}
                        >
                          {id}
                        </span>
                        <span className={`font-bold truncate ${current ? "text-white" : ""}`}>
                          {lesson.title}
                        </span>
                      </span>

                      {done && !current ? (
                        <CheckCircle size={18} className="text-emerald-600 dark:text-emerald-200 shrink-0" />
                      ) : null}
                      {current ? <Clock size={18} className="shrink-0 animate-pulse text-white" /> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      <Footer />
    </div>
  );
}
